import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Phone } from "lucide-react";

const PHONE_LINK = "tel:+33549057490";

export const PannePricing = ({ titleH1, pricing }) => (
  <section className="section-padding bg-white" data-testid="panne-pricing">
    <div className="container-custom max-w-4xl">
      <div className="text-center mb-10">
        <Badge className="bg-red-100 text-red-700 px-4 py-2 mb-4 hover:bg-red-100">Tarifs transparents</Badge>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          Tarifs {titleH1.toLowerCase().replace("à niort", "").trim()}
        </h2>
        <p className="text-lg text-slate-600">
          Pièces premium + main-d&apos;œuvre incluses. Garantie 6 mois.
        </p>
      </div>

      <Card className="overflow-hidden border-2 border-slate-200">
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="text-left px-4 md:px-6 py-4 font-semibold">Modèle / Service</th>
                <th className="text-right px-4 md:px-6 py-4 font-semibold">Tarif</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pricing.map((row) => (
                <tr key={row.model} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 md:px-6 py-4 text-slate-800">{row.model}</td>
                  <td className="px-4 md:px-6 py-4 text-right font-bold text-red-600">{row.price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="text-center text-sm text-slate-500 mt-6">
        * Tarifs indicatifs. Devis ferme après diagnostic gratuit. Main-d&apos;œuvre Mac : 79 €/h.
      </p>
    </div>
  </section>
);

export const PanneFaq = ({ faqs }) => (
  <section className="section-padding bg-slate-50" data-testid="panne-faq">
    <div className="container-custom max-w-4xl">
      <div className="text-center mb-10">
        <Badge className="bg-red-100 text-red-700 px-4 py-2 mb-4 hover:bg-red-100">FAQ</Badge>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          Questions fréquentes
        </h2>
      </div>

      <div className="space-y-4">
        {faqs.map((f) => (
          <div key={f.q} className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-bold text-lg text-slate-900 mb-2">{f.q}</h3>
            <p className="text-slate-700 leading-relaxed">{f.a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const PanneLocalisation = () => (
  <section className="section-padding bg-white">
    <div className="container-custom max-w-4xl">
      <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <MapPin className="h-6 w-6 text-red-600" />
            Où nous trouver à Niort
          </h2>
          <div className="grid md:grid-cols-2 gap-6 text-slate-700">
            <div>
              <p className="font-semibold text-slate-900 mb-1">Mediastore Niort</p>
              <p className="text-sm">104 Rue de la Gare, 79000 Niort</p>
              <p className="text-sm">À 200 m de la gare SNCF</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">Horaires</p>
              <p className="text-sm">Lundi-Vendredi : 10h-18h</p>
              <p className="text-sm">Samedi : 10h-16h</p>
            </div>
          </div>
          <p className="text-slate-600 mt-6 leading-relaxed text-sm">
            Mediastore intervient également pour les habitants de <strong>Chauray</strong>,
            <strong> Aiffres</strong>, <strong>La Crèche</strong>, <strong>Saint-Maixent-l&apos;École</strong>,
            <strong> Parthenay</strong>, <strong>Bressuire</strong>, <strong>La Rochelle</strong>,
            <strong> Poitiers</strong> et toutes les communes dans un rayon de 80 km.{" "}
            <Link to="/villes-desservies" className="text-red-600 underline">Voir toutes les villes</Link>.
          </p>
        </CardContent>
      </Card>
    </div>
  </section>
);

export const PanneCta = () => (
  <section className="py-12 bg-gradient-to-r from-red-600 to-red-700 text-white">
    <div className="container-custom text-center">
      <h2 className="text-3xl md:text-4xl font-bold mb-4">
        Besoin d&apos;un diagnostic gratuit ?
      </h2>
      <p className="text-lg text-red-100 mb-8 max-w-2xl mx-auto">
        Appelez-nous, prenez rendez-vous en ligne ou utilisez notre service Click &amp; Collect.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a href={PHONE_LINK}>
          <Button size="lg" className="bg-white text-red-600 hover:bg-red-50 h-14 px-8 gap-2 text-lg font-semibold" data-testid="panne-cta-call">
            <Phone className="h-5 w-5" />
            05 49 05 74 90
          </Button>
        </a>
        <Link to="/click-collect">
          <Button size="lg" variant="outline" className="h-14 px-8 gap-2 border-2 border-white text-white hover:bg-white/10" data-testid="panne-cta-cc">
            Click &amp; Collect
          </Button>
        </Link>
      </div>
    </div>
  </section>
);
